import React, { useEffect } from "react";
import { NumericFormat } from "react-number-format";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { Combobox } from "../../../Components/ui/combobox";
import { Button } from "../../../Components/ui/button";
import { ModalClose } from "../../../Components/ui/modal";
import Spinner from "../../../Assets/spinner";
import { VEHICLE_PRICE_FIELDS, pairKey } from "../utils/vehicles";

const vehiclePrice = yup.number().min(0, "Price can't be negative");

// A price of 0 means that vehicle doesn't serve the pair, but at least one
// vehicle must.
const ZonePriceValidationSchema = (mode) =>
  yup
    .object()
    .shape({
      originZoneId:
        mode === "add"
          ? yup.string().required("Please select an origin zone")
          : yup.string(),
      destinationZoneId:
        mode === "add"
          ? yup.string().required("Please select a destination zone")
          : yup.string(),
      ...Object.fromEntries(
        VEHICLE_PRICE_FIELDS.map(({ field }) => [field, vehiclePrice]),
      ),
    })
    .test("at-least-one-price", function (values) {
      const hasPrice = VEHICLE_PRICE_FIELDS.some(({ field }) => values[field] > 0);
      return (
        hasPrice ||
        this.createError({
          path: "pricesRequired",
          message: "Set a price for at least one vehicle",
        })
      );
    });

const ZonePriceForm = ({
  mode = "add",
  zoneOptions = [],
  existingPairKeys = new Set(),
  originZoneName,
  destinationZoneName,
  initialValues,
  submitting,
  onSubmit,
}) => {
  const formik = useFormik({
    initialValues,
    validationSchema: ZonePriceValidationSchema(mode),
    onSubmit,
  });

  const { handleSubmit, values, errors, touched, submitCount, setFieldValue } =
    formik;

  // Same-zone pricing (e.g. Zone 1 -> Zone 1) is allowed, so the selected zone
  // is not excluded here — only pairs that already have prices are hidden;
  // those are changed with Edit.
  const getAvailableOptions = (selectedId) =>
    zoneOptions.filter(
      (option) =>
        !selectedId || !existingPairKeys.has(pairKey(selectedId, option.value)),
    );

  const originOptions =
    mode === "add" ? getAvailableOptions(values.destinationZoneId) : zoneOptions;
  const destinationOptions =
    mode === "add" ? getAvailableOptions(values.originZoneId) : zoneOptions;

  useEffect(() => {
    if (
      mode === "add" &&
      values.destinationZoneId &&
      !destinationOptions.some((option) => option.value === values.destinationZoneId)
    ) {
      setFieldValue("destinationZoneId", "");
    }
  }, [values.originZoneId]);

  useEffect(() => {
    if (
      mode === "add" &&
      values.originZoneId &&
      !originOptions.some((option) => option.value === values.originZoneId)
    ) {
      setFieldValue("originZoneId", "");
    }
  }, [values.destinationZoneId]);

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <div className='w-[80vw] md:w-full flex flex-col gap-4'>
          {mode === "add" ? (
            <>
              <div>
                <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                  Origin Zone
                </label>
                <Combobox
                  search
                  frameworks={originOptions}
                  setValue={(val) => setFieldValue("originZoneId", val?.value)}
                  value={values.originZoneId}
                  placeholder='Origin Zone'
                />
                {errors.originZoneId && touched.originZoneId && (
                  <p className='text-xs text-red mt-1'>
                    <ErrorMessage name='originZoneId' />
                  </p>
                )}
              </div>

              <div>
                <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                  Destination Zone
                </label>
                <Combobox
                  search
                  frameworks={destinationOptions}
                  setValue={(val) =>
                    setFieldValue("destinationZoneId", val?.value)
                  }
                  value={values.destinationZoneId}
                  placeholder='Destination Zone'
                />
                {errors.destinationZoneId && touched.destinationZoneId && (
                  <p className='text-xs text-red mt-1'>
                    <ErrorMessage name='destinationZoneId' />
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className='flex items-center gap-2 text-sm text-primaryBlack'>
              <span className='font-semibold'>{originZoneName}</span>
              <span className='text-primaryAsh'>&rarr;</span>
              <span className='font-semibold'>{destinationZoneName}</span>
            </div>
          )}

          <div>
            <p className='text-xs font-semibold text-HeaderColor'>Prices</p>
            <p className='text-xs text-[#667499] mt-0.5 mb-2'>
              ₦0 means that vehicle isn&apos;t available for this route.
            </p>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              {VEHICLE_PRICE_FIELDS.map(({ field, label }) => (
                <div key={field}>
                  <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                    {label}
                  </label>
                  <NumericFormat
                    className='p-2 outline-none bg-transparent border border-[#C2C7D6] rounded-md w-full placeholder:text-sm'
                    placeholder='₦0'
                    allowNegative={false}
                    decimalScale={0}
                    prefix='₦'
                    thousandSeparator=','
                    value={values[field]}
                    onValueChange={({ floatValue }) =>
                      setFieldValue(field, floatValue ?? 0)
                    }
                  />
                  {errors[field] && (
                    <p className='text-xs text-red mt-1'>{errors[field]}</p>
                  )}
                </div>
              ))}
            </div>
            {errors.pricesRequired && submitCount > 0 && (
              <p className='text-xs text-red mt-1'>{errors.pricesRequired}</p>
            )}
          </div>

          <div className='mt-4 flex justify-between'>
            <ModalClose>
              <Button variant='outline' type='button'>
                Cancel
              </Button>
            </ModalClose>
            <Button type='submit' disabled={submitting}>
              {submitting ? <Spinner /> : "Save"}
            </Button>
          </div>
        </div>
      </form>
    </FormikProvider>
  );
};

export default ZonePriceForm;
